import { hashKey } from "@tanstack/react-query";

import { setOrgTimezone } from "simplydo/core/dates";
import {
  userIsFollowingContext,
  userIsFollowingContextObject,
} from "simplydo/core/following";
import { setInterpolationDictionary } from "simplydo/core/i18n";
import type { OpenAPI } from "simplydo/interfaces";
import type {
  UserMe,
  UserMeOwnerOrganisation,
} from "simplydo/interfaces/types/userTypes";
import { useCurrentUser as useApiCurrentUser } from "simplydo/react/hooks/useCurrentUser";

import api from "api";

export type { UserMe } from "simplydo/interfaces/types/userTypes";

export const useCurrentUser = () => useApiCurrentUser(api);

// Ghost token is in-memory only; a leftover revert_token after reload is the admin session, not ghosting
export const isGhosting = () => !!api.revertToken;

/** Ghost and unGhost both refetch the user, so subscribing to it re-renders on change. */
export const useIsGhosting = () => {
  useCurrentUser();
  return isGhosting();
};

// Applies terminology and timezone whenever the cached organisation changes, whichever code path updated it
let appliedOrganisation: UserMeOwnerOrganisation | undefined;
api.queryClient.getQueryCache().subscribe(({ type, query }) => {
  if (
    type !== "updated" ||
    query.queryHash !== hashKey(api.currentUserQueryKey())
  ) {
    return;
  }
  const organisation = (query.state.data as UserMe | null)?.ownerOrganisation;
  if (organisation === appliedOrganisation) return;
  appliedOrganisation = organisation;
  // No organisation means the session ended; drop the previous names
  if (!organisation) {
    setInterpolationDictionary();
    return;
  }
  setInterpolationDictionary(organisation.terminology);
  setOrgTimezone(organisation.defaultTimezone);
});

/** Shallow-merges into the signed-in user; does nothing when signed out. */
export const updateCurrentUser = (
  patch: Partial<UserMe> | ((user: UserMe) => Partial<UserMe>),
) => {
  const user = api.getCurrentUser();
  if (!user) return;
  api.setCurrentUser({
    ...user,
    ...(typeof patch === "function" ? patch(user) : patch),
  });
};

export const userCache = {
  createGroup(id: string, name: string) {
    updateCurrentUser((user) => {
      const permissions = { ...user.permissions };
      permissions[id] = [...(permissions[id] ?? []), "group.editSettings"];
      return {
        groups: [...(user.groups || []), { _id: id, name }],
        permissions,
      };
    });
  },
  leaveGroup(group: { _id: string }) {
    updateCurrentUser((user) => ({
      groups: (user.groups || []).filter((g) => g._id !== group._id),
      permissions: { ...user.permissions, [group._id]: [] },
    }));
  },
  updateProfile(profile: Partial<NonNullable<UserMe["profile"]>>) {
    updateCurrentUser((user) => ({ profile: { ...user.profile, ...profile } }));
  },
  switchOrganisation(organisation: UserMeOwnerOrganisation) {
    // We temporarily maintain organisation and ownerOrganisation separately
    updateCurrentUser(() => ({
      organisation: organisation._id,
      ownerOrganisation: organisation,
    }));
  },
  updateOrganisation(field: string, value: unknown) {
    const user = api.getCurrentUser();
    if (!user?.organisation) return;
    // Dynamic organisation field updates — ownerOrganisation holds the full object
    updateCurrentUser(() => ({
      ownerOrganisation: {
        ...user.ownerOrganisation,
        [field]: value,
      } as UserMeOwnerOrganisation,
    }));
  },
  addEngagement(engagement: string) {
    updateCurrentUser(({ engagements = [] }) =>
      engagements.includes(engagement)
        ? {}
        : { engagements: [...engagements, engagement] },
    );
  },
  updatePermissions(scope: string, permissions: string[]) {
    updateCurrentUser((user) => ({
      permissions: {
        ...(user.permissions || {}),
        [scope]: permissions,
      } as UserMe["permissions"],
    }));
  },
  updateTablePreferences(key: string, preferences: unknown) {
    updateCurrentUser((user) => ({
      tablePreferences: {
        ...user.tablePreferences,
        [key]: preferences,
      } as UserMe["tablePreferences"],
    }));
  },
  updateSavedChallengeIdeaFilters(
    savedChallengeIdeaFilters: NonNullable<UserMe["savedChallengeIdeaFilters"]>,
  ) {
    updateCurrentUser(() => ({ savedChallengeIdeaFilters }));
  },
  updateTags(tags: OpenAPI.GET<"/tags">["response"]) {
    updateCurrentUser(() => ({
      tags: tags.map((t) => t._id),
      ownerTags: tags,
    }));
  },
  onFollow(context: string, id: string, manual = false) {
    updateCurrentUser((user) => {
      if (userIsFollowingContext(user, context, id)) return {};
      const followingContextObject = userIsFollowingContextObject(
        user,
        context,
        id,
      );
      if (followingContextObject.unfollowed && !manual) return {};
      return {
        following: [
          { ...followingContextObject, following: true, unfollowed: false },
          ...(user.following || []).filter((f) => f._id !== id),
        ],
      };
    });
  },
  addIdeaId(ideaId: string) {
    updateCurrentUser((user) => ({
      following: [
        ...(user.following || []),
        {
          type: "idea",
          _id: ideaId,
          following: true,
          createdAt: new Date().toISOString(),
          reason: "ideaCreated",
        },
      ],
    }));
  },
};
